import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tss } from 'tss-react/mui';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { AgentDto, HandoffRuleRequestDto } from '@shared/services/apiService/apiService';
import CustomAlert from '@shared/components/alert/alert';
import { SQUAD_MESSAGES } from '@features/Admin/Agent/constants/squad.constants';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { HandoffDrawer } from '@features/Admin/Agent/ui/HandoffCanvas';
import { HandoffJsonPanel, HandoffStructuredPanel } from './HandoffRulesEditorPanels';
import { useHandoffRulesEditorState } from '@features/Admin/Agent/handoffRules/state/useHandoffRulesEditorState';
import { TabSectionHeader } from '@pages/Admin/Campaign/ui/SidebarTabs/TabSectionHeader';
import { IconHeadphones } from '@shared/assets/images/svgs';
import { SidebarTabNames } from '@pages/Admin/Campaign/ui/constants';

const useStyles = tss.create(({ theme }) => ({
  kbSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    position: 'relative',
    // Keep sticky toolbar above React Flow's transformed layers.
    isolation: 'isolate',
  },
  toolbar: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: theme.zIndex.appBar,
  },
  canvasLayer: {
    position: 'relative',
    zIndex: 0,
  },
  label: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '19.6px',
  },
}));

interface HandoffRulesEditorProps {
  value: string;
  onChange: (value: string) => void;
  isDisabled: boolean;
  agents: AgentDto[];
  hideIntroText?: boolean;
  canvasHeight?: number | string;
  jsonEditorHeight?: number | string;
  isSaving?: boolean;
  showSaveButton?: boolean;
  saveDisabled?: boolean;
  onSave?: (
    payload: HandoffRuleRequestDto[],
  ) => Promise<{ ok: boolean; message?: string } | void> | void;
  onDirtyChange?: (isDirty: boolean) => void;
  onValidationChange?: (hasErrors: boolean) => void;
  onActionsChange?: (actions: { save: () => Promise<boolean>; discard: () => void } | null) => void;
  onRequestAgentsTab?: () => boolean | void;
}

export const HandoffRulesEditor: React.FC<HandoffRulesEditorProps> = ({
  value,
  onChange,
  isDisabled,
  agents,
  hideIntroText = false,
  canvasHeight,
  jsonEditorHeight,
  isSaving = false,
  showSaveButton = true,
  saveDisabled = false,
  onSave,
  onDirtyChange,
  onValidationChange,
  onActionsChange,
  onRequestAgentsTab,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [responseMsg, setResponseMsg] = useState<{ message: string; type: 'success' | 'error' }>({
    message: '',
    type: 'success',
  });
  const [rulesMenuAnchor, setRulesMenuAnchor] = useState<null | HTMLElement>(null);
  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [isAgentDrawerOpen, setIsAgentDrawerOpen] = useState(false);
  const [isMissingRulesDialogOpen, setIsMissingRulesDialogOpen] = useState(false);
  const [hasAcknowledgedMissingRulesWarning, setHasAcknowledgedMissingRulesWarning] =
    useState(false);
  const saveStatusTimeoutRef = useRef<number | null>(null);
  const handoffIssueMessages = useMemo(
    () => ({
      missingFromAgent:
        t('campaignPage.squads.editor.validation.missingFromAgent') || 'From agent is required.',
      missingToAgent:
        t('campaignPage.squads.editor.validation.missingToAgent') || 'To agent is required.',
      duplicateDirection:
        t('campaignPage.squads.editor.validation.duplicateDirection') ||
        'From agent and To agent must be different.',
      duplicateRoute:
        t('campaignPage.squads.editor.validation.duplicateRoute') ||
        'Duplicate routes between the same agents are not allowed.',
      invalidContextMaxMessages:
        t('campaignPage.squads.editor.validation.invalidContextMaxMessages') ||
        'Max messages must be 1 or more.',
      invalidExtractionRootType:
        t('campaignPage.squads.editor.validation.invalidExtractionRootType') ||
        'Variable extraction schema root must be an object.',
      invalidExtractionAdditionalProperties:
        t('campaignPage.squads.editor.validation.invalidExtractionAdditionalProperties') ||
        'Variable extraction schema must set additionalProperties=false.',
      invalidExtractionRequiredReference:
        t('campaignPage.squads.editor.validation.invalidExtractionRequiredReference') ||
        'Variable extraction required fields must exist in properties.',
      invalidExtractionPropertyName:
        t('campaignPage.squads.editor.validation.invalidExtractionPropertyName') ||
        'Variable extraction property names cannot be empty.',
      invalidExtractionArrayItems:
        t('campaignPage.squads.editor.validation.invalidExtractionArrayItems') ||
        'Array extraction fields must define an items schema.',
      emptyMessageValue:
        t('campaignPage.squads.editor.validation.emptyMessageValue') ||
        'Message groups cannot contain empty strings.',
      duplicateEndingAgents:
        t('campaignPage.squads.editor.validation.duplicateEndingAgents') ||
        'Ending agent list cannot contain duplicates.',
    }),
    [t],
  );
  const [lastFocusedSectionByRuleId, setLastFocusedSectionByRuleId] = useState<
    Record<string, 'route' | 'destinationContext' | 'variableExtraction' | 'toolMessages'>
  >({});
  const getAgentDisplayName = useCallback((agent: AgentDto) => agent.name?.trim() || agent.id, []);
  const {
    jsonError,
    editorMode,
    setEditorMode,
    rules,
    saveState: _saveState,
    setSaveState,
    canvasAgentIds,
    nodePositions,
    updateNodePosition,
    isAssistantPickerOpen,
    setIsAssistantPickerOpen,
    assistantSearch,
    setAssistantSearch,
    selectedRuleId,
    isDrawerOpen,
    setIsDrawerOpen,
    focusedSection,
    setFocusedSection,
    isDirty,
    hasAgentsWithoutRules,
    hasValidationErrors,
    selectedRule,
    canRecoverJson,
    handleJsonChange,
    revertToLastValidJson,
    formatJsonValue,
    updateRule,
    addRule,
    removeRule,
    openDrawerForRule,
    commitDrawerChanges,
    discardDrawerChanges,
    discardUnsavedChanges,
    handleSelectAssistant,
    addConnectedRule,
    serializeCurrentValue,
    markCurrentAsSaved,
    pruneUnusedCanvasAgents,
    seedAutoLayoutPositionsIfEmpty,
  } = useHandoffRulesEditorState({
    value,
    onChange,
    onDirtyChange,
    onValidationChange,
    invalidJsonMessage: t('common.invalidJson') || 'Invalid JSON',
    issueMessages: handoffIssueMessages,
  });
  const isSaveBlocked =
    isDisabled || isSaving || Boolean(jsonError) || hasValidationErrors || !isDirty || saveDisabled;

  useEffect(() => {
    seedAutoLayoutPositionsIfEmpty(agents);
  }, [agents, rules, canvasAgentIds, nodePositions, seedAutoLayoutPositionsIfEmpty]);

  const canvasAgents = agents.filter((agent) => canvasAgentIds.includes(agent.id));
  const agentOptions = agents.map((agent) => ({ id: agent.id, label: getAgentDisplayName(agent) }));
  const shouldShowAssistantSearch = agents.length > 6;
  const filteredAssistants = useMemo(
    () =>
      agents.filter((agent) =>
        getAgentDisplayName(agent).toLowerCase().includes(assistantSearch.toLowerCase()),
      ),
    [agents, assistantSearch, getAgentDisplayName],
  );
  const allAssistantsOnCanvas =
    agents.length > 0 && agents.every((agent) => canvasAgentIds.includes(agent.id));
  const availableAssistants = filteredAssistants.filter(
    (agent) => !canvasAgentIds.includes(agent.id),
  );
  const getAvatarColorByName = useCallback((name: string) => {
    const palettes = [
      { bg: '#e0f2fe', fg: '#0c4a6e' },
      { bg: '#ede9fe', fg: '#4c1d95' },
      { bg: '#dcfce7', fg: '#14532d' },
      { bg: '#fef3c7', fg: '#78350f' },
      { bg: '#fee2e2', fg: '#7f1d1d' },
      { bg: '#fce7f3', fg: '#831843' },
    ];
    let hash = 0;
    for (let index = 0; index < name.length; index += 1) {
      hash = (hash << 5) - hash + name.charCodeAt(index);
      hash |= 0;
    }
    return palettes[Math.abs(hash) % palettes.length];
  }, []);
  const handleAddAssistant = () => {
    if (isDisabled) return;
    setIsDrawerOpen(false);
    setIsAgentDrawerOpen(false);
    setIsAssistantPickerOpen(true);
  };

  const handleOpenRuleDrawer = useCallback(
    (
      ruleId: string,
      section: 'route' | 'destinationContext' | 'variableExtraction' | 'toolMessages',
    ) => {
      setIsAssistantPickerOpen(false);
      setIsAgentDrawerOpen(false);
      openDrawerForRule(ruleId, section);
    },
    [openDrawerForRule, setIsAssistantPickerOpen],
  );

  const handleConnectAgents = (sourceAgentId: string, targetAgentId: string) => {
    if (isDisabled) return;
    const existingRule = rules.find(
      (rule) => rule.fromAgentId === sourceAgentId && rule.toAgentId === targetAgentId,
    );
    if (existingRule) {
      openDrawerForRule(existingRule.id, lastFocusedSectionByRuleId[existingRule.id] || 'route');
      return;
    }

    addConnectedRule(sourceAgentId, targetAgentId, (agentId, fallback) => {
      return agentOptions.find((agent) => agent.id === agentId)?.label || fallback;
    });
  };
  const handleFocusedSectionChange = useCallback(
    (section: 'route' | 'destinationContext' | 'variableExtraction' | 'toolMessages') => {
      setFocusedSection(section);
      if (selectedRuleId) {
        setLastFocusedSectionByRuleId((prev) => ({ ...prev, [selectedRuleId]: section }));
      }
    },
    [selectedRuleId, setFocusedSection],
  );
  const openRuleFromMenu = useCallback(
    (ruleId: string) => {
      setRulesMenuAnchor(null);
      setHoveredRuleId(null);
      // Wait for Menu backdrop to unmount so the first Cancel click isn't swallowed.
      window.setTimeout(() => {
        handleOpenRuleDrawer(ruleId, lastFocusedSectionByRuleId[ruleId] || 'route');
      }, 0);
    },
    [handleOpenRuleDrawer, lastFocusedSectionByRuleId],
  );

  const closeRuleDrawer = useCallback(() => {
    setRulesMenuAnchor(null);
    setHoveredRuleId(null);
    discardDrawerChanges();
  }, [discardDrawerChanges]);

  useEffect(() => {
    setHasAcknowledgedMissingRulesWarning(false);
    setIsMissingRulesDialogOpen(false);
  }, [value, hasAgentsWithoutRules]);

  const executeSave = useCallback(async () => {
    if (isSaveBlocked || !onSave) return false;

    pruneUnusedCanvasAgents();
    const payload: HandoffRuleRequestDto[] = serializeCurrentValue();
    setSaveState('saving');
    try {
      const result = await onSave(payload);
      if (!result || typeof result !== 'object' || !('ok' in result) || result.ok !== true) {
        setSaveState('error');
        setResponseMsg({
          message:
            (result && typeof result === 'object' && 'message' in result && result.message) ||
            t(SQUAD_MESSAGES.HANDOFF_RULES_SAVE_FAILED),
          type: 'error',
        });
        return false;
      }
      markCurrentAsSaved();
      setResponseMsg({
        message: result.message || t(SQUAD_MESSAGES.HANDOFF_RULES_SAVED),
        type: 'success',
      });
      if (saveStatusTimeoutRef.current) {
        window.clearTimeout(saveStatusTimeoutRef.current);
      }
      saveStatusTimeoutRef.current = window.setTimeout(() => {
        setSaveState('idle');
        setResponseMsg({ message: '', type: 'success' });
        saveStatusTimeoutRef.current = null;
      }, 2000);
      return true;
    } catch {
      setSaveState('error');
      setResponseMsg({
        message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVE_FAILED),
        type: 'error',
      });
      return false;
    }
  }, [
    isSaveBlocked,
    markCurrentAsSaved,
    onSave,
    pruneUnusedCanvasAgents,
    serializeCurrentValue,
    setSaveState,
    t,
  ]);

  const handleSave = useCallback(async () => {
    if (isSaveBlocked || !onSave) return false;
    if (hasAgentsWithoutRules && !hasAcknowledgedMissingRulesWarning) {
      setIsMissingRulesDialogOpen(true);
      return false;
    }

    return executeSave();
  }, [
    executeSave,
    hasAcknowledgedMissingRulesWarning,
    hasAgentsWithoutRules,
    isSaveBlocked,
    onSave,
  ]);

  useEffect(() => {
    onActionsChange?.({
      save: handleSave,
      discard: discardUnsavedChanges,
    });
    return () => onActionsChange?.(null);
  }, [discardUnsavedChanges, handleSave, onActionsChange]);

  const handleConfirmMissingRulesSave = async () => {
    setHasAcknowledgedMissingRulesWarning(true);
    setIsMissingRulesDialogOpen(false);
    await executeSave();
  };

  const handleCloseMissingRulesDialog = () => {
    setIsMissingRulesDialogOpen(false);
  };
  useEffect(
    () => () => {
      if (saveStatusTimeoutRef.current) {
        window.clearTimeout(saveStatusTimeoutRef.current);
      }
    },
    [],
  );
  const headerInfoTooltip = (
    <Tooltip
      title={
        <Stack spacing={0.75} sx={{ p: 0.5 }}>
          {!hideIntroText && (
            <>
              <Typography variant="body2">{t('campaignPage.squads.editor.description')}</Typography>
              <Typography variant="body2">{t('campaignPage.squads.editor.modeHint')}</Typography>
              <Typography variant="body2">
                {t('campaignPage.squads.editor.structuredHint')}
              </Typography>
            </>
          )}
        </Stack>
      }>
      <IconButton size="small" aria-label={t('campaignPage.squads.editor.title')}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box className={classes.kbSection}>
      <TabSectionHeader
        icon={IconHeadphones}
        title={t('campaignPage.squads.editor.title')}
        trailing={headerInfoTooltip}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="nowrap"
        useFlexGap
        className={classes.toolbar}
        sx={{
          minHeight: 48,
          px: 1,
          pb: 1,
          mb: 1,
          gap: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="nowrap"
          useFlexGap
          sx={{ minWidth: 0, overflowX: 'auto' }}>
          <ToggleButtonGroup
            exclusive
            value={editorMode}
            size="small"
            onChange={(_, nextMode: 'structured' | 'json' | null) => {
              if (nextMode) setEditorMode(nextMode);
            }}>
            <ToggleButton value="structured">
              {t('campaignPage.squads.editor.modes.structured')}
            </ToggleButton>
            <ToggleButton value="json">{t('campaignPage.squads.editor.modes.json')}</ToggleButton>
          </ToggleButtonGroup>
          {editorMode === 'structured' && (
            <>
              {canvasAgentIds.length > 0 && (
                <Button
                  onClick={handleAddAssistant}
                  disabled={isDisabled}
                  variant="contained"
                  startIcon={<AddIcon />}>
                  {t('campaignPage.squads.editor.actions.addAssistant')}
                </Button>
              )}
              <Button
                onClick={addRule}
                disabled={isDisabled}
                variant="outlined"
                startIcon={<AddIcon />}>
                {t('campaignPage.squads.editor.actions.addRule')}
              </Button>
              <Button
                variant="outlined"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(event) => setRulesMenuAnchor(event.currentTarget)}>
                {t('campaignPage.squads.editor.rulesCount', { count: rules.length })}
              </Button>
            </>
          )}
        </Stack>
        <Box sx={{ flex: 1, minWidth: 8 }} />
        {isDirty && (
          <Tooltip title={t('campaignPage.squads.editor.states.dirty')}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
          </Tooltip>
        )}
        {showSaveButton && (
          <Tooltip title={isDirty ? '' : t('campaignPage.squads.editor.states.saved')}>
            <span>
              <Button
                variant="contained"
                size="small"
                disabled={isSaveBlocked || !onSave}
                onClick={handleSave}>
                {t('adminMainPage.operation.save')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Box className={classes.canvasLayer}>
        <HandoffStructuredPanel
          isOpen={editorMode === 'structured'}
          rules={rules}
          canvasAgentIds={canvasAgentIds}
          canvasAgents={canvasAgents}
          nodePositions={nodePositions}
          selectedRuleId={selectedRuleId}
          isDisabled={isDisabled}
          onOpenRule={(ruleId) =>
            handleOpenRuleDrawer(ruleId, lastFocusedSectionByRuleId[ruleId] || 'route')
          }
          onConnectAgents={handleConnectAgents}
          onNodePositionChange={(agentId, position) => {
            if (isDisabled) return;
            updateNodePosition(agentId, position);
          }}
          highlightedRuleId={hoveredRuleId}
          isDirty={isDirty}
          onNodeSelect={(agentId) => {
            if (isDisabled) return;
            setSelectedAgentId(agentId);
            setIsAgentDrawerOpen(true);
          }}
          onAddAssistant={handleAddAssistant}
          canvasHeight={canvasHeight}
        />

        <HandoffJsonPanel
          isOpen={editorMode === 'json'}
          value={value}
          onChange={handleJsonChange}
          isDisabled={isDisabled}
          jsonError={jsonError}
          onRevertToLastValid={revertToLastValidJson}
          onFormatJson={formatJsonValue}
          canRecoverJson={canRecoverJson}
          jsonEditorHeight={jsonEditorHeight}
        />
      </Box>
      <HandoffDrawer
        open={isDrawerOpen && Boolean(selectedRule)}
        onClose={closeRuleDrawer}
        focusedSection={focusedSection}
        onFocusedSectionChange={handleFocusedSectionChange}
        rule={selectedRule}
        isDisabled={isDisabled}
        agentOptions={agentOptions}
        onUpdateRule={updateRule}
        onDeleteRule={(ruleId) => {
          removeRule(ruleId);
          setIsDrawerOpen(false);
        }}
        onSaveChanges={commitDrawerChanges}
        onCancel={closeRuleDrawer}
      />
      <Drawer
        anchor="right"
        open={isAgentDrawerOpen}
        onClose={() => setIsAgentDrawerOpen(false)}
        slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
        PaperProps={{
          sx: {
            width: 360,
            p: 2,
          },
        }}>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('agentPage.agentDetails')}</Typography>
            <IconButton size="small" onClick={() => setIsAgentDrawerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider />
          <Typography variant="subtitle1" fontWeight={600}>
            {selectedAgentId
              ? agentOptions.find((agent) => agent.id === selectedAgentId)?.label || selectedAgentId
              : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('campaignPage.squads.editor.agentNodeHint')}
          </Typography>
          {selectedAgentId
            ? rules
                .filter(
                  (rule) =>
                    rule.fromAgentId === selectedAgentId || rule.toAgentId === selectedAgentId,
                )
                .map((rule, index) => (
                  <Stack
                    key={rule.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {rule.name ||
                        t('campaignPage.squads.editor.ruleChipLabel', {
                          index: index + 1,
                          from:
                            agentOptions.find((agent) => agent.id === rule.fromAgentId)?.label ||
                            rule.fromAgentId,
                          to:
                            agentOptions.find((agent) => agent.id === rule.toAgentId)?.label ||
                            rule.toAgentId,
                        })}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setIsAgentDrawerOpen(false);
                        handleOpenRuleDrawer(
                          rule.id,
                          lastFocusedSectionByRuleId[rule.id] || 'route',
                        );
                      }}>
                      {t('common.edit')}
                    </Button>
                  </Stack>
                ))
            : null}
        </Stack>
      </Drawer>
      <Drawer
        anchor="left"
        open={isAssistantPickerOpen}
        onClose={() => setIsAssistantPickerOpen(false)}
        PaperProps={{
          sx: {
            width: 340,
            p: 1.5,
            height: 'fit-content',
            maxHeight: 'calc(100vh - 24px)',
            top: 12,
            bottom: 'auto',
            borderRadius: 2,
          },
        }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {t('campaignPage.squads.editor.addAssistantDrawer.title')}
            </Typography>
            <IconButton size="small" onClick={() => setIsAssistantPickerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack spacing={0.75}>
            {allAssistantsOnCanvas ? (
              <Stack spacing={1.5} sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('campaignPage.squads.editor.addAssistantDrawer.allOnCanvas')}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setIsAssistantPickerOpen(false);
                    if (onRequestAgentsTab) {
                      onRequestAgentsTab();
                      return;
                    }
                    navigate(`?tab=${SidebarTabNames.AGENT}`);
                  }}>
                  {t('campaignPage.squads.editor.addAssistantDrawer.goToAgents')}
                </Button>
              </Stack>
            ) : (
              <>
                {shouldShowAssistantSearch && (
                  <CustomTextField
                    fullWidth
                    size="small"
                    value={assistantSearch}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setAssistantSearch(event.target.value)
                    }
                    placeholder={t(
                      'campaignPage.squads.editor.addAssistantDrawer.searchPlaceholder',
                    )}
                  />
                )}
                <Stack spacing={0.5}>
                  {filteredAssistants.map((agent) => {
                    const name = getAgentDisplayName(agent);
                    const onCanvas = canvasAgentIds.includes(agent.id);
                    const avatarColor = getAvatarColorByName(name);
                    return (
                      <Box
                        key={agent.id}
                        sx={{
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          minHeight: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          '&:hover': { backgroundColor: '#f8fafc' },
                        }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: 13,
                              bgcolor: avatarColor.bg,
                              color: avatarColor.fg,
                            }}>
                            {name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{name}</Typography>
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={onCanvas}
                          onClick={() => handleSelectAssistant(agent.id)}>
                          {onCanvas
                            ? t('campaignPage.squads.editor.addAssistantDrawer.onCanvas')
                            : t('campaignPage.squads.editor.addAssistantDrawer.add')}
                        </Button>
                      </Box>
                    );
                  })}
                </Stack>
                {availableAssistants.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('campaignPage.squads.editor.addAssistantDrawer.empty')}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Drawer>
      <Dialog
        open={isMissingRulesDialogOpen}
        onClose={handleCloseMissingRulesDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="handoff-missing-rules-dialog-title">
        <DialogTitle
          id="handoff-missing-rules-dialog-title"
          sx={{ fontSize: '24px', fontWeight: 600, pt: 3 }}>
          {t('campaignPage.squads.editor.missingRulesDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('campaignPage.squads.editor.missingRulesDialog.description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseMissingRulesDialog}
            sx={{ textTransform: 'none', fontSize: '15px' }}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMissingRulesSave}
            sx={{ textTransform: 'none', fontSize: '15px' }}>
            {t('action.saveChange')}
          </Button>
        </DialogActions>
      </Dialog>
      {responseMsg.message && <CustomAlert message={responseMsg.message} type={responseMsg.type} />}
      <Menu
        anchorEl={rulesMenuAnchor}
        open={Boolean(rulesMenuAnchor)}
        onClose={() => {
          setRulesMenuAnchor(null);
          setHoveredRuleId(null);
        }}>
        {rules.map((rule, index) => (
          <MenuItem
            key={rule.id}
            onMouseEnter={() => setHoveredRuleId(rule.id)}
            onMouseLeave={() => setHoveredRuleId(null)}
            onClick={() => {
              openRuleFromMenu(rule.id);
            }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              width="100%">
              <Typography variant="body2">
                {t('campaignPage.squads.editor.ruleChipLabel', {
                  index: index + 1,
                  from:
                    agentOptions.find((agent) => agent.id === rule.fromAgentId)?.label || 'From',
                  to: agentOptions.find((agent) => agent.id === rule.toAgentId)?.label || 'To',
                })}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={(event) => {
                  event.stopPropagation();
                  openRuleFromMenu(rule.id);
                }}>
                {t('common.edit')}
              </Button>
              <Button
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  removeRule(rule.id);
                }}>
                {t('common.delete')}
              </Button>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
